import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import { admissionService } from '../../services/admissionService';
import { 
  Search, 
  CheckCircle, 
  Clock, 
  XCircle, 
  AlertCircle,
  Calendar,
  User,
  School,
  Phone,
  Mail,
  MapPin,
  FileText,
  Download,
  Eye,
  ArrowLeft,
  RefreshCw
} from 'lucide-react';

const ApplicationStatus = () => {
  const [searchParams] = useSearchParams();
  const [applicationNumber, setApplicationNumber] = React.useState(searchParams.get('applicationNumber') || '');
  const [application, setApplication] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [searched, setSearched] = React.useState(false);

  const fetchApplicationStatus = async () => {
    if (!applicationNumber.trim()) {
      setError('Please enter an application number');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await admissionService.getApplicationStatus(applicationNumber);
      setApplication(response.data.data);
      setSearched(true);
    } catch (e) {
      setError(e.response?.data?.message || 'Application not found');
      setApplication(null);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (applicationNumber && !searched) {
      fetchApplicationStatus();
    }
  }, [applicationNumber]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'under_review':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'pending':
        return <AlertCircle className="w-5 h-5 text-blue-500" />;
      case 'waitlisted':
        return <Clock className="w-5 h-5 text-orange-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'under_review':
        return 'bg-yellow-100 text-yellow-800';
      case 'pending':
        return 'bg-blue-100 text-blue-800';
      case 'waitlisted':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusMessage = (status) => {
    switch (status) {
      case 'approved':
        return 'Congratulations! Your application has been approved. You will receive further instructions via email.';
      case 'rejected':
        return 'We regret to inform you that your application was not successful this time. You may reapply in the next admission cycle.';
      case 'under_review':
        return 'Your application is currently under review. We will notify you of the decision soon.';
      case 'pending':
        return 'Your application has been received and is pending review.';
      case 'waitlisted':
        return 'Your application has been placed on the waitlist. We will contact you if a spot becomes available.';
      default:
        return 'Application status is being processed.';
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (date) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Application Status</h1>
              <p className="text-gray-600">Check your admission application status</p>
            </div>
            <Button variant="outline" onClick={() => window.history.back()}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Search Form */}
        {!application && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Check Application Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="applicationNumber">Application Number</Label>
                  <div className="flex space-x-2 mt-1">
                    <Input
                      id="applicationNumber"
                      value={applicationNumber}
                      onChange={(e) => setApplicationNumber(e.target.value)}
                      placeholder="Enter your application number"
                      onKeyPress={(e) => e.key === 'Enter' && fetchApplicationStatus()}
                    />
                    <Button onClick={fetchApplicationStatus} disabled={loading}>
                      {loading ? (
                        <LoadingSpinner />
                      ) : (
                        <>
                          <Search className="w-4 h-4 mr-2" />
                          Search
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                {error && (
                  <div className="text-red-600 text-sm">{error}</div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Application Details */}
        {application && (
          <div className="space-y-6">
            {/* Status Overview */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center">
                    {getStatusIcon(application.status)}
                    <span className="ml-2">Application Status</span>
                  </CardTitle>
                  <Badge className={getStatusColor(application.status)}>
                    {application.status.replace('_', ' ').toUpperCase()}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-gray-700">{getStatusMessage(application.status)}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
                    <div className="text-center">
                      <div className="flex items-center justify-center space-x-1 mb-1">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <span className="font-semibold">{formatDate(application.submittedAt)}</span>
                      </div>
                      <p className="text-sm text-gray-600">Submitted</p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center space-x-1 mb-1">
                        <Clock className="w-4 h-4 text-gray-500" />
                        <span className="font-semibold">
                          {application.lastUpdatedAt ? formatDate(application.lastUpdatedAt) : 'N/A'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">Last Updated</p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center space-x-1 mb-1">
                        <FileText className="w-4 h-4 text-gray-500" />
                        <span className="font-semibold">{application.applicationNumber}</span>
                      </div>
                      <p className="text-sm text-gray-600">Application Number</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Application Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <User className="w-5 h-5 mr-2" />
                  Application Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-600">Student Name</Label>
                      <p className="text-lg font-semibold">
                        {application.firstName} {application.middleName} {application.lastName}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-600">Date of Birth</Label>
                      <p>{formatDate(application.dateOfBirth)}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-600">Gender</Label>
                      <p className="capitalize">{application.gender}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-600">Nationality</Label>
                      <p>{application.nationality}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-600">Desired Class</Label>
                      <p className="text-lg font-semibold">{application.desiredClass}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-600">Academic Year</Label>
                      <p>{application.academicYear}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-600">Previous School</Label>
                      <p>{application.previousSchool}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-600">Last Class Completed</Label>
                      <p>{application.lastClassCompleted}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Phone className="w-5 h-5 mr-2" />
                  Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-600">Email</Label>
                      <p className="flex items-center">
                        <Mail className="w-4 h-4 mr-2 text-gray-500" />
                        {application.email}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-600">Phone</Label>
                      <p className="flex items-center">
                        <Phone className="w-4 h-4 mr-2 text-gray-500" />
                        {application.phone}
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-600">Address</Label>
                      <p className="flex items-start">
                        <MapPin className="w-4 h-4 mr-2 text-gray-500 mt-0.5" />
                        {application.address}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-600">Emergency Contact</Label>
                      <p>{application.emergencyContactName} - {application.emergencyContactPhone}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Interview Information */}
            {application.interview && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Calendar className="w-5 h-5 mr-2" />
                    Interview Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium text-gray-600">Interview Date</Label>
                        <p className="font-semibold">{formatDate(application.interview.date)}</p>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-gray-600">Interview Time</Label>
                        <p className="font-semibold">{application.interview.time}</p>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-gray-600">Location</Label>
                        <p>{application.interview.location}</p>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-gray-600">Interviewer</Label>
                        <p>{application.interview.interviewer}</p>
                      </div>
                    </div>
                    {application.interview.notes && (
                      <div>
                        <Label className="text-sm font-medium text-gray-600">Notes</Label>
                        <p className="mt-1">{application.interview.notes}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Documents */}
            {application.documents && application.documents.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <FileText className="w-5 h-5 mr-2" />
                    Submitted Documents
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {application.documents.map((doc, index) => (
                      <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center space-x-3">
                          <FileText className="w-4 h-4 text-gray-500" />
                          <div>
                            <p className="font-medium">{doc.name}</p>
                            <p className="text-sm text-gray-500">{doc.type}</p>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Button variant="outline" size="sm">
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          <Button variant="outline" size="sm">
                            <Download className="w-4 h-4 mr-1" />
                            Download
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Actions */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-wrap gap-4">
                  <Button onClick={fetchApplicationStatus} variant="outline">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh Status
                  </Button>
                  
                  {application.status === 'approved' && (
                    <>
                      <Button>
                        <Download className="w-4 h-4 mr-2" />
                        Download Admission Letter
                      </Button>
                      <Button variant="outline">
                        <Download className="w-4 h-4 mr-2" />
                        Download Receipt
                      </Button>
                    </>
                  )}
                  
                  {application.interview && (
                    <Button variant="outline">
                      <Calendar className="w-4 h-4 mr-2" />
                      Reschedule Interview
                    </Button>
                  )}
                  
                  <Button variant="outline">
                    <Mail className="w-4 h-4 mr-2" />
                    Contact Admission Office
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplicationStatus;
